import { DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { AssetDto, GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { combineLatest, distinctUntilKeyChanged, filter, of, switchMap } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { GROWTHBOOK } from '../../../growthbook';
import { debounce } from '../../../lib/debounce';
import { fixContentEditableStyles } from '../../../lib/fix-contenteditable-styles';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { AssetService } from '../../../services/asset.service';
import { CleanUserGeneratedHtmlService } from '../../../services/clean-user-generated-html.service';
import { ExifService } from '../../../services/exif.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { TagService } from '../../../services/tag.service';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ClickTrackDirective } from '../../../shared/directives/click-track.directive';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';
import { FloorPipe } from '../../../shared/pipes/floor.pipe';
import { MegapixelsPipe } from '../../../shared/pipes/megapixels.pipe';
import { ShutterSpeedPipe } from '../../../shared/pipes/shutter-speed.pipe';
import { VideoResolutionTypePipe } from '../../../shared/pipes/video-resolution-type.pipe';
import { removeHtml } from '../../../storyblok/custom-render-text';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { AssetTagsComponent } from '../asset-tags/asset-tags.component';
import { AssetVersionMenuComponent } from '../asset-version-menu/asset-version-menu.component';

@Component({
  selector: 'app-asset-info',
  templateUrl: './asset-info.component.html',
  styleUrls: ['./asset-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    WysiwygDirective,
    SvgComponent,
    AssetTagsComponent,
    ContextMenuComponent,
    AssetVersionMenuComponent,
    UpperCasePipe,
    DatePipe,
    MegapixelsPipe,
    BillingScopePipe,
    ClickTrackDirective,
    FloorPipe,
    VideoResolutionTypePipe,
    DecimalPipe,
    DataUnitsBigIntPipe,
    AvatarComponent,
    ShutterSpeedPipe,
  ],
})
export class AssetInfoComponent {
  private exifService = inject(ExifService);
  private galleryService = inject(GalleryService);
  private state = inject(StateService);
  private assetService = inject(AssetService);
  private _sanitizer = inject(DomSanitizer);
  private cleanUserGeneratedHtmlService = inject(CleanUserGeneratedHtmlService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private tagService = inject(TagService);
  private permissionService = inject(PermissionService);
  public growthBook = inject(GROWTHBOOK);
  @ViewChild('description', { static: false }) description: ElementRef<HTMLDivElement>;
  readonly asset = input<AssetDto>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  readonly galleryPermissions = input<GalleryDto['permissions']>(undefined);
  readonly tenant = computed(() => {
    return (this.gallery().tenant as TenantDto)?.id
      ? (this.gallery().tenant as TenantDto)
      : this.state.activeTenant$?.value;
  });
  readonly exif = computed(() => {
    const asset = this.asset();
    if (!asset) return;
    return this.exifService.getExif(asset);
  });
  readonly allowVersionHistory = computed(() => {
    const gallery = this.gallery();
    const galleryPermissions = this.galleryPermissions();

    if (!this.permissionService.canDisplayElement(billingScopes.assetVersion.multiple)) {
      return false;
    }
    return gallery.version_history_enabled || galleryPermissions.can_edit;
  });
  readonly assetName = linkedSignal(() => {
    const asset = this.asset();
    if (!asset) return '';
    const name = this.cleanUserGeneratedHtmlService.clean(asset.name || '');
    if (name === '\n') return '';
    return this._sanitizer.sanitize(SecurityContext.NONE, name);
  });
  readonly newAssetName = linkedSignal(() => this.assetName());

  readonly assetDescription = linkedSignal(() => {
    const asset = this.asset();
    const exif = this.exif();
    if (!asset || !exif) return '';
    const description = this.cleanUserGeneratedHtmlService.clean(asset.description || exif?.description || '');
    if (description === '\n') return '';
    return this._sanitizer.sanitize(SecurityContext.NONE, description);
  });
  readonly newAssetDescription = linkedSignal(() => this.assetDescription());

  readonly tenantTags = toSignal(this.tagService.tags$);
  readonly creator = toSignal(
    combineLatest([
      toObservable(this.asset).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
      toObservable(this.gallery).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
      toObservable(this.tenant).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
    ]).pipe(
      switchMap(([asset, gallery, tenant]) => {
        return this.assetService.getCreator(asset.version as string, gallery.id).pipe(
          switchMap(creator => {
            if (creator === null) {
              return this.galleryService.getGalleryOwner(
                gallery.id,
                this.state.tenants?.some(t => t.id === tenant.id)
              );
            }
            return of(creator);
          })
        );
      })
    )
  );

  private async saveName() {
    const asset = this.asset();
    const name = removeHtml(this.newAssetName()).trim();
    if (!name) return;
    this.analytics.track('save_name');
    await this.assetService.updateAsset(asset, { name }, <string>asset.gallery);
  }

  private async saveDescription() {
    const asset = this.asset();
    let description = fixContentEditableStyles(this.newAssetDescription());
    if (description === '') description = '\n';
    if (description === '/auto') description = '';
    this.analytics.track('save_description');
    await this.assetService.updateAsset(asset, { description }, <string>asset.gallery);
  }

  saveNameDebounced = debounce(() => {
    this.saveName();
  }, 500);

  saveDescriptionDebounced = debounce(() => {
    this.saveDescription();
  }, 500);

  convertFrameRate(frameRate: string) {
    if (!frameRate) return 30;
    const [numerator, denominator] = frameRate.toString().split('/');
    if (!denominator) return Number(numerator);
    return Math.round(parseInt(numerator) / parseInt(denominator));
  }
}
